import { useCallback, useState } from "react";
import { IntegrationName } from "../../../../api/integration/Integration.model";
import { StepHeader, StepMain } from "../parts";
import { integrationNameToGalleryItem } from "../../../../api/integration/integrationNameToGalleryItem";
import { ArticleCategoryContent } from "@/v2/features/article-category";
import { Flex, Text, Button, Spinner } from "@radix-ui/themes";
import styles from "../AddIntegration.module.scss";
import { useArticleCategorySelector } from "@/v2/shared/hooks/useArticleCategorySelector";

interface ConfigureCategoriesProps {
  name: IntegrationName;
  onSubmit: () => void;
  onBack: () => void;
}

function ConfigureCategories({
  name,
  onSubmit,
  onBack,
}: ConfigureCategoriesProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the article category selector hook to get the save function
  const articleCategorySelector = useArticleCategorySelector({
    source: name,
    onClose: () => {}, // No need to close since we're not using a dialog
  });

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      // Save the knowledge configuration
      await articleCategorySelector.handleSaveConfiguration();
      // Proceed to next step
      onSubmit();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  }, [articleCategorySelector.handleSaveConfiguration, onSubmit]);

  const title = `Configure Article Categories for ${integrationNameToGalleryItem[name].title}`;

  return (
    <StepMain>
      <Flex pt={"35px"} px={"45px"}>
        <StepHeader title={title} />
      </Flex>

      <Flex flexGrow={"1"} overflow={"hidden"} pt={"16px"}>
        <div className={styles.Form}>
          <div className={styles.FieldList}>
            <div className={styles.FieldControl}>
              <Text size={"1"} weight={"medium"} as={"label"}>
                Article Category Selection
              </Text>
              <Text size={"1"} color={"gray"} mb={"2"}>
                Select which article categories to include in your knowledge
                base integration.
              </Text>

              <ArticleCategoryContent
                {...articleCategorySelector}
                source={name}
                onSave={onSubmit}
                onClose={onBack}
                showButtons={false}
              />
            </div>
          </div>

          <div className={styles.Footer}>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={handleSubmit}
              disabled={isSubmitting || articleCategorySelector.isSaving}
            >
              <Spinner
                loading={isSubmitting || articleCategorySelector.isSaving}
              />
              Continue
            </Button>

            <Button
              color="gray"
              variant="soft"
              highContrast
              type="button"
              onClick={onBack}
            >
              Back
            </Button>
          </div>
        </div>
      </Flex>
    </StepMain>
  );
}

export default ConfigureCategories;
